import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  BarChart3, Loader2, ChevronDown, ChevronUp, Users,
  CheckCircle2, AlertTriangle, XCircle, Calendar, Phone,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_SHORT = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

const CoverageComparison = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasRules, setHasRules] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);

  // Default to current week Mon-Fri
  const getDefaultRange = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mon = new Date(today);
    mon.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const fri = new Date(mon);
    fri.setDate(mon.getDate() + 4);
    return {
      start: mon.toISOString().split('T')[0],
      end: fri.toISOString().split('T')[0],
    };
  };

  const [range, setRange] = useState(getDefaultRange);

  useEffect(() => {
    fetchComparison();
  }, [range]);

  const shiftRange = (direction) => {
    setRange(prev => {
      const s = new Date(prev.start + 'T12:00:00');
      const e = new Date(prev.end + 'T12:00:00');
      const days = direction * 7;
      s.setDate(s.getDate() + days);
      e.setDate(e.getDate() + days);
      return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] };
    });
  };

  const fetchComparison = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/staffing-rules/coverage-comparison?start_date=${range.start}&end_date=${range.end}`,
        { headers }
      );
      if (res.data.error) {
        setHasRules(false);
        setData(null);
      } else {
        setHasRules(true);
        setData(res.data);
      }
    } catch {
      toast.error('Failed to load coverage comparison');
    } finally {
      setLoading(false);
    }
  };

  if (!hasRules) {
    return (
      <Card data-testid="coverage-comparison-empty">
        <CardContent className="py-8 text-center">
          <BarChart3 className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-700">No Staffing Rules Configured</p>
          <p className="text-xs text-slate-500 mt-1">
            Go to <strong>Admin &gt; Staffing Rules</strong> to define time blocks and role requirements.
            The AI and this comparison view will use those rules.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading && !data) {
    return (
      <Card data-testid="coverage-comparison-loading">
        <CardContent className="py-6 flex items-center justify-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /><span className="text-sm">Loading coverage...</span>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const summary = data.summary || {};

  const statusIcon = (status) => {
    if (status === 'met') return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />;
    if (status === 'critical') return <XCircle className="h-3.5 w-3.5 text-red-500" />;
    return <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />;
  };

  const statusColor = (status) => {
    if (status === 'met') return 'bg-emerald-50 border-emerald-200';
    if (status === 'critical') return 'bg-red-50 border-red-200';
    return 'bg-amber-50 border-amber-200';
  };

  const formatDate = (d) => {
    const dt = new Date(d + 'T12:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Card data-testid="coverage-comparison">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-indigo-500" />
            Coverage vs Requirements
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => shiftRange(-1)} data-testid="prev-week">
              &larr; Prev
            </Button>
            <span className="text-xs text-slate-600 font-medium">
              {formatDate(range.start)} — {formatDate(range.end)}
            </span>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => shiftRange(1)} data-testid="next-week">
              Next &rarr;
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary bar */}
        <div className="flex items-center gap-4 text-xs flex-wrap">
          <div className="flex items-center gap-1.5">
            <div className={`h-2.5 w-2.5 rounded-full ${
              summary.coverage_pct >= 90 ? 'bg-emerald-500' : summary.coverage_pct >= 60 ? 'bg-amber-500' : 'bg-red-500'
            }`} />
            <span className="font-semibold text-slate-700" data-testid="coverage-pct">{summary.coverage_pct}% blocks met</span>
          </div>
          <span className="text-slate-400">|</span>
          <span className="text-slate-500">{summary.blocks_met}/{summary.total_blocks} time blocks covered</span>
          {summary.total_gaps > 0 && (
            <>
              <span className="text-slate-400">|</span>
              <span className="text-red-600 font-medium" data-testid="total-gaps">{summary.total_gaps} role gaps</span>
            </>
          )}
          {summary.on_call_blocks_total > 0 && (
            <>
              <span className="text-slate-400">|</span>
              <div className="flex items-center gap-1.5">
                <Phone className="h-3 w-3 text-orange-500" />
                <span className="font-semibold text-orange-700" data-testid="oncall-coverage-pct">
                  On-Call: {summary.on_call_coverage_pct}% met
                </span>
                {summary.on_call_total_gaps > 0 && (
                  <span className="text-orange-600">({summary.on_call_total_gaps} gaps)</span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Day-by-day grid */}
        <div className="grid grid-cols-1 gap-2">
          {(data.days || []).map(day => {
            const isExpanded = expandedDay === day.date;
            const allMet = day.blocks.every(b => b.status === 'met');
            const hasCritical = day.blocks.some(b => b.status === 'critical');
            return (
              <div
                key={day.date}
                className={`rounded-lg border transition-all ${
                  allMet ? 'border-emerald-200 bg-emerald-50/30' :
                  hasCritical ? 'border-red-200 bg-red-50/30' : 'border-amber-200 bg-amber-50/30'
                }`}
                data-testid={`day-row-${day.date}`}
              >
                {/* Day header */}
                <button
                  onClick={() => setExpandedDay(isExpanded ? null : day.date)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-left"
                  data-testid={`toggle-day-${day.date}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-slate-800 w-20">
                      {DAY_SHORT[day.day_name]} {formatDate(day.date)}
                    </span>
                    {day.pto_count > 0 && (
                      <Badge variant="outline" className="text-[10px] text-orange-600 border-orange-300">
                        {day.pto_count} PTO
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Block status pills */}
                    <div className="flex gap-1 flex-wrap">
                      {day.blocks.map((b, i) => (
                        <React.Fragment key={i}>
                          <div
                            className={`px-2 py-0.5 rounded text-[10px] font-medium border ${statusColor(b.status)}`}
                            title={`${b.label}: ${b.total_scheduled}/${b.total_required}`}
                          >
                            <span className="flex items-center gap-1">
                              {statusIcon(b.status)}
                              {b.start_time}-{b.end_time}: {b.total_scheduled}/{b.total_required}
                            </span>
                          </div>
                          {b.on_call_status && b.on_call_status !== 'none' && (
                            <div
                              className={`px-2 py-0.5 rounded text-[10px] font-medium border ${statusColor(b.on_call_status)}`}
                              title={`On-Call ${b.label}: ${b.on_call_total_scheduled}/${b.on_call_total_required}`}
                            >
                              <span className="flex items-center gap-1">
                                <Phone className="h-2.5 w-2.5 text-orange-500" />
                                {b.on_call_total_scheduled}/{b.on_call_total_required}
                              </span>
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                  </div>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="px-4 pb-3 space-y-2 border-t border-slate-200/60">
                    {day.blocks.map((block, i) => (
                      <div key={i} className="mt-2" data-testid={`block-detail-${block.block_id}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          {statusIcon(block.status)}
                          <span className="text-xs font-semibold text-slate-700">
                            {block.label || `${block.start_time} — ${block.end_time}`}
                          </span>
                        </div>

                        {/* Role breakdown table */}
                        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="text-left px-3 py-1.5 font-medium text-slate-500">Role</th>
                                <th className="text-center px-3 py-1.5 font-medium text-slate-500">Required</th>
                                <th className="text-center px-3 py-1.5 font-medium text-slate-500">Scheduled</th>
                                <th className="text-center px-3 py-1.5 font-medium text-slate-500">Gap</th>
                                <th className="text-center px-3 py-1.5 font-medium text-slate-500">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(block.required).map(([role, needed]) => {
                                const have = block.scheduled[role] || 0;
                                const gap = Math.max(0, needed - have);
                                const over = Math.max(0, have - needed);
                                return (
                                  <tr key={role} className="border-b border-slate-50 last:border-0">
                                    <td className="px-3 py-1.5 font-medium text-slate-700 capitalize">{role}</td>
                                    <td className="px-3 py-1.5 text-center text-slate-600">{needed}</td>
                                    <td className={`px-3 py-1.5 text-center font-semibold ${have >= needed ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {have}
                                    </td>
                                    <td className="px-3 py-1.5 text-center">
                                      {gap > 0 ? (
                                        <span className="text-red-600 font-semibold">-{gap}</span>
                                      ) : over > 0 ? (
                                        <span className="text-emerald-600">+{over}</span>
                                      ) : (
                                        <span className="text-slate-400">—</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-1.5 text-center">
                                      {gap > 0 ? (
                                        <Badge variant="destructive" className="text-[10px]">Understaffed</Badge>
                                      ) : over > 0 ? (
                                        <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Over</Badge>
                                      ) : (
                                        <Badge variant="secondary" className="text-[10px]">Met</Badge>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Scheduled employees */}
                        {block.employees?.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {block.employees.map((emp, j) => (
                              <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                <Users className="h-2.5 w-2.5 inline mr-0.5" />
                                {emp.name} ({emp.role}) {emp.time}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* On-Call section */}
                        {block.on_call_status && block.on_call_status !== 'none' && (
                          <div className="mt-3" data-testid={`oncall-detail-${block.block_id}`}>
                            <div className="flex items-center gap-2 mb-1.5">
                              <Phone className="h-3.5 w-3.5 text-orange-500" />
                              <span className="text-xs font-semibold text-orange-700">
                                On-Call Coverage
                              </span>
                              {statusIcon(block.on_call_status)}
                            </div>

                            <div className="overflow-hidden rounded-md border border-orange-200 bg-white">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-orange-50 border-b border-orange-100">
                                    <th className="text-left px-3 py-1.5 font-medium text-orange-600">Role</th>
                                    <th className="text-center px-3 py-1.5 font-medium text-orange-600">Required</th>
                                    <th className="text-center px-3 py-1.5 font-medium text-orange-600">Scheduled</th>
                                    <th className="text-center px-3 py-1.5 font-medium text-orange-600">Gap</th>
                                    <th className="text-center px-3 py-1.5 font-medium text-orange-600">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.entries(block.on_call_required || {}).map(([role, needed]) => {
                                    const have = (block.on_call_scheduled || {})[role] || 0;
                                    const gap = Math.max(0, needed - have);
                                    const over = Math.max(0, have - needed);
                                    return (
                                      <tr key={role} className="border-b border-orange-50 last:border-0">
                                        <td className="px-3 py-1.5 font-medium text-slate-700 capitalize">{role}</td>
                                        <td className="px-3 py-1.5 text-center text-slate-600">{needed}</td>
                                        <td className={`px-3 py-1.5 text-center font-semibold ${have >= needed ? 'text-emerald-600' : 'text-red-600'}`}>
                                          {have}
                                        </td>
                                        <td className="px-3 py-1.5 text-center">
                                          {gap > 0 ? (
                                            <span className="text-red-600 font-semibold">-{gap}</span>
                                          ) : over > 0 ? (
                                            <span className="text-emerald-600">+{over}</span>
                                          ) : (
                                            <span className="text-slate-400">—</span>
                                          )}
                                        </td>
                                        <td className="px-3 py-1.5 text-center">
                                          {gap > 0 ? (
                                            <Badge variant="destructive" className="text-[10px]">Understaffed</Badge>
                                          ) : over > 0 ? (
                                            <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Over</Badge>
                                          ) : (
                                            <Badge variant="secondary" className="text-[10px]">Met</Badge>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            {/* On-call employees */}
                            {block.on_call_employees?.length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {block.on_call_employees.map((emp, j) => (
                                  <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                                    <Phone className="h-2.5 w-2.5 inline mr-0.5" />
                                    {emp.name} ({emp.role}) {emp.time}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {(data.days || []).length === 0 && (
          <div className="text-center py-4 text-xs text-slate-400">
            No staffing rules defined for these dates. Add rules for the relevant days in Admin &gt; Staffing Rules.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CoverageComparison;
